import React from 'react';
import { motion } from 'framer-motion';
import { useCourse } from '../context/CourseContext';
import { ChapterCard } from '../components/course/ChapterCard';
import { Progress } from '../components/ui/progress';
import { BookOpen, Award, Clock } from 'lucide-react';

export const ChaptersPage = () => {
  const { chapters, overallProgress, getChapterProgress } = useCourse();

  const completedCount = chapters.filter(ch => getChapterProgress(ch.id).completed).length;
  const totalReadTime = chapters.reduce((acc, ch) => {
    const minutes = parseInt(ch.duration.match(/\d+/)?.[0] || 0, 10);
    return acc + minutes;
  }, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-silver-shimmer/10 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center mb-12"
        >
          <div className="maroon-accent mx-auto mb-6" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Your Learning Journey
          </h1>
          <p className="text-muted-foreground text-lg">
            Five transformational steps to reclaim your authentic power
          </p>
        </motion.div>

        {/* Progress Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-silver/20 rounded-xl p-6 mb-12 max-w-4xl mx-auto shadow-sm"
        >
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shadow-glow">
                <Award className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{overallProgress}%</p>
                <p className="text-sm text-muted-foreground">Complete</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center shadow-gold">
                <BookOpen className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{completedCount}/{chapters.length}</p>
                <p className="text-sm text-muted-foreground">Chapters Done</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-maroon/10 flex items-center justify-center shadow-maroon">
                <Clock className="w-6 h-6 text-maroon" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">~{Math.round(totalReadTime / 60)}h</p>
                <p className="text-sm text-muted-foreground">Total Content</p>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Overall Progress</span>
              <span className="text-primary font-medium">{overallProgress}%</span>
            </div>
            <Progress value={overallProgress} className="h-2" />
          </div>
        </motion.div>

        {/* Chapter Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {chapters.map((chapter, index) => (
            <ChapterCard key={chapter.id} chapter={chapter} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChaptersPage;
